import { useEffect, useMemo, useState } from "react"

// libraries
import { Popover, Checkbox, FormControlLabel } from "@mui/material"
import Skeleton from "react-loading-skeleton"

// helpers
import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"

// images
import rightArrowImage from "@/assets/img/storage/icons/right-arrow.svg"
import selectArrowImage from "@/assets/img/icons/select-arrow.svg"
import backArrowImage from "@/assets/img/reservations/icons/left-arrow.svg"

const MAX_VISIBLE_BRANDS = 2

export default function CategoryBrandSelect(props) {
  const [categories, setCategories] = useState([])
  const [brands, setBrands] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [loadingBrands, setLoadingBrands] = useState(true)

  const value = props.value || {}
  const categoryId = value.category_id || ""
  const brandIds = useMemo(
    () => (Array.isArray(value.brand_prices) ? value.brand_prices : []),
    [value.brand_prices],
  )

  const [anchorEl, setAnchorEl] = useState(null)
  const [view, setView] = useState("categories")
  const [draft, setDraft] = useState({ category_id: "", brand_prices: [] })

  // зарежда категориите (с техните марки)
  useEffect(() => {
    fetchCategories()
    fetchBrands()
  }, [])

  // зарежда текущите данни в работното състояние при отваряне на падащото меню
  useEffect(() => {
    if (anchorEl) {
      setDraft({ category_id: categoryId, brand_prices: [...brandIds] })
      setView(categoryId ? "brands" : "categories")
    }
  }, [anchorEl, categoryId, brandIds])

  function fetchCategories() {
    setLoadingCategories(true)

    Api.get("appliances/categories/all")
      .then((res) => {
        setCategories(res.data.items || [])
      })
      .finally(() => setLoadingCategories(false))
  }

  function fetchBrands() {
    setLoadingBrands(true)
    
    Api.get("appliances/brands/all")
      .then((res) => {
        setBrands(res.data.items || [])
      })
      .catch((err) => {
        console.error(trans("messages.errorFetchingBrands"), err)
      })
      .finally(() => setLoadingBrands(false))
  }

  const getCategory = (id) =>
    categories.find((c) => String(c.id) === String(id)) || null

  const getCategoryName = (id) => {
    const category = getCategory(id)
    return category ? category.name : ""
  }

  // const getCategoryBrands = (id) => {
  //   const category = getCategory(id)
  //   return category && Array.isArray(category.brands) ? category.brands : []
  // }

  const getBrandName = (brandId) => {
    const brand = brands.find((b) => String(b.id) === String(brandId))
    return brand ? brand.name : ""
  }

  const handleSelectCategory = (id) => {
    setDraft((prev) => ({
      category_id: id,
      // при смяна на категория - изчистваме избраните марки
      brand_prices: String(prev.category_id) === String(id) ? prev.brand_prices : [],
    }))
    setView("brands")
  }

  const handleToggleBrand = (id) => {
    setDraft((prev) => {
      const exists = prev.brand_prices.some((b) => String(b) === String(id))
      return {
        ...prev,
        brand_prices: exists
          ? prev.brand_prices.filter((b) => String(b) !== String(id))
          : [...prev.brand_prices, id],
      }
    })
  }

  const handleSave = () => {
    if (typeof props.onChange === "function") {
      props.onChange({
        category_id: draft.category_id,
        brand_prices: draft.brand_prices,
      })
    }
    setAnchorEl(null)
  }

  const renderValue = () => {
    if (!categoryId) {
      return <span className="placeholder">{trans("selfEmployed.categoryBrandPlaceholder")}</span>
    }

    return (
      <>
        <span className="category">{getCategoryName(categoryId)}</span>
        <img src={rightArrowImage} alt="" className="separator" />
        {brandIds.length === 0 ? (
          <span className="brands">{trans("selfEmployed.allBrands")}</span>
        ) : (
          <span className="brands">
            {brandIds
              .slice(0, MAX_VISIBLE_BRANDS)
              .map((id) => getBrandName(id))
              .filter(Boolean)
              .join(", ")}
            {brandIds.length > MAX_VISIBLE_BRANDS && (
              <span className="more-pill">
                +{brandIds.length - MAX_VISIBLE_BRANDS}
              </span>
            )}
          </span>
        )}
      </>
    )
  }

  return (
    <>
      {/* скрити полета за изпращане на формата */}
      {props.name && (
        <>
          <input
            type="hidden"
            name={`${props.name}[category_id]`}
            value={categoryId}
          />
          {brandIds.map((id) => (
            <input
              key={`hidden-brand-${id}`}
              type="hidden"
              name={`${props.name}[brand_id][]`}
              value={id}
            />
          ))}
        </>
      )}

      <div
        className="category-brand-select"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <div className="value">{renderValue()}</div>
        <img src={selectArrowImage} alt={trans("buttons.open")} className="chevron" />
      </div>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        className="category-brand-popover"
        sx={{ zIndex: 100002 }}
      >
        <div className="popover-inner">
          {view === "categories" ? (
            <>
              <b className="head">{trans("selfEmployed.selectCategory")}</b>
              {loadingCategories ? (
                <>
                  <Skeleton count={1} height={30} />
                  <Skeleton count={1} height={30} />
                  <Skeleton count={1} height={30} />
                </>
              ) : (
                <ul className="options">
                  {categories.map((c) => (
                    <li
                      key={c.id}
                      className={
                        String(draft.category_id) === String(c.id)
                          ? "active"
                          : ""
                      }
                      onClick={() => handleSelectCategory(c.id)}
                    >
                      <span className="label">{c.name}</span>
                      <img src={rightArrowImage} alt={trans("buttons.select")} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className="head with-back">
                <img
                  src={backArrowImage}
                  alt={trans("buttons.back")}
                  className="back"
                  onClick={() => setView("categories")}
                />
                <b>{getCategoryName(draft.category_id)}</b>
              </div>
              {loadingBrands ? (
                <>
                  <Skeleton count={1} height={30} />
                  <Skeleton count={1} height={30} />
                  <Skeleton count={1} height={30} />
                </>
              ) : (
                <>
                  <ul className="options brands">
                    {brands.map((b) => (
                      <li
                        key={b.id}
                        onClick={() => handleToggleBrand(b.id)}
                        style={{
                          paddingLeft: "10px",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={draft.brand_prices.some(
                                (id) => String(id) === String(b.id),
                              )}
                              size="small"
                              sx={{ margin: "-6px 0" }}
                            />
                          }
                          label={b.name}
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                          onChange={(e) => {
                            handleToggleBrand(b.id)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                  <div className="footer">
                    <button
                      type="button"
                      className="save button-small"
                      onClick={handleSave}
                    >
                      {trans("buttons.save")}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Popover>
    </>
  )
}
